import React, {useCallback, useState} from 'react'
import {
  Alerts,
  type DownloadPdfResult,
  If,
  RPCDocs,
  useCurrentUserAnPCheck,
  useDownloadsApiServiceContext,
  UserAnPCheck,
  useRPCContext,
  useToggle,
} from '@therms/react-modules'
import {ReportTitle} from '@src/modules/Reports/components/ViewReport/components/ReportTitle'
import {
  ActionModal,
  Button,
  ContextMenu,
  Icon,
  Loading,
  Modal,
  toastManager,
  Tooltip,
} from '@therms/atalaya'
import {ChangeReportStatusModal} from '@src/modules/Reports/components/ViewReport/components/ChangeReportStatusModal'
import {ShowStatusHistoryModal} from '@src/modules/Reports/components/ViewReport/components/ShowStatusHistoryModal'
import {PickReportDate} from '@src/modules/Reports/components/PickReportDate'
import {useHistory} from 'react-router-dom'
import {downloadBase64AsPdf} from '@therms/web-js'
import dayjs from 'dayjs'
import {PickReportRefsModal} from '@src/modules/Reports/components/ViewReport/components/PickReportRefsModal'
import {MdRefresh} from 'react-icons/md'

interface Props {
  formBuilderValidation?: {[key: string]: string} | undefined
  hasUnsavedFormChanges?: boolean
  loading?: boolean
  onSave?: (params: {date?: string}) => void
  refreshReport: () => void
  report: RPCDocs.Reports.GetReport.v1.Report
  reportType: RPCDocs.Reports.GetReport.v1.ReportTypeType
}

function ReportHeaderSection({
  formBuilderValidation,
  hasUnsavedFormChanges,
  loading,
  onSave,
  refreshReport,
  report,
  reportType,
}: Props) {
  const canDownloadAndExportData = useCurrentUserAnPCheck(
    ({anp}) => anp.role.ops.access.canDownloadAndExportData,
  )
  const {client} = useDownloadsApiServiceContext()
  const {rpcService} = useRPCContext()
  const history = useHistory()

  const [showStatusHistoryModal, toggleShowStatusHistoryModal] =
    useToggle(false)
  const [showChangeStatusModal, toggleShowChangeStatusModal] = useToggle(false)
  const [showDeleteModal, toggleShowDeleteModal] = useToggle(false)
  const [showDownloadModal, toggleShowDownloadModal] = useToggle(false)
  const [showEditDateModal, toggleShowEditDateModal] = useToggle(false)

  const [date, setDate] = useState(report.date)

  const deleteReport = async () =>
    rpcService.Reports.DeleteReport.v1
      .call({reportId: report.id})
      .then(() => history.goBack())
      .catch((err: any) => {
        console.log('Reports.DeleteReport.v1.call() err', err)

        Alerts.error('There was a problem deleting the report', err.message)
      })

  const PermissionsCheck = useCallback(
    ({children}) => (
      <UserAnPCheck
        check={(user) =>
          report.authors?.find(({id}) => id === user.id) ||
          user.anp.role.ops.permissions.editOtherUserReports
        }
      >
        {children}
      </UserAnPCheck>
    ),

    [report],
  )

  const handleDownloadReportPDF = (values: {
    showInternalContent: boolean
    showEventsRefs: boolean
    showLocationsRefs: boolean
    showLogEntriesRefs: boolean
    showRecordsRefs: boolean
    showReportsRefs: boolean
    showUsersRefs: boolean
    withRefHistory?: boolean
  }) => {
    const toastId = Alerts.info(
      <div>
        <Icon Icon={MdRefresh} spin /> Starting download, please wait...
      </div>,
      {autoClose: false, position: 'top-center'},
    )

    client.reports.DownloadReport(
      {
        body: {
          reportId: report.id,
          showEventsRefs: values.showEventsRefs,
          showInternalContent: values.showInternalContent,
          showLocationsRefs: values.showLocationsRefs,
          showLogEntriesRefs: values.showLogEntriesRefs,
          showRecordsRefs: values.showRecordsRefs,
          showReportsRefs: values.showReportsRefs,
          showUsersRefs: values.showUsersRefs,
          withRefHistory: values.withRefHistory,
        },
      },
      {timeout: 60_000},
    )
      .then((response) => {
        const result = response as DownloadPdfResult
        if (result.pdfBase64) {
          downloadBase64AsPdf(
            `${dayjs(report.date).format()} ${report.title} ${
              report.subTitle
            } ${report.authors
              ?.map(({displayName}) => displayName)
              .join(', ')}`,
            result.pdfBase64,
          )
        }
      })
      .catch((err: any) => {
        console.error('err downloading report', err)
      })
      .finally(() => toastManager.dismiss(toastId))
  }
  const canChangeStatus =
    reportType.inputConfig.type === 'multi-entry' ||
    (reportType.inputConfig.type === 'single-entry' &&
      !formBuilderValidation &&
      !hasUnsavedFormChanges)

  return (
    <>
      {report.deleted && (
        <div className="bg-critical-faded text-color-critical mb-sm p-sm border-critical rounded border text-center font-semibold">
          This Report has been deleted
        </div>
      )}

      <div>
        {loading && !report && <Loading overlay />}

        <div className="gap-x-sm inline-flex w-full justify-between">
          <ReportTitle report={report} />

          <div className="inline-flex">
            <If truthy={!report.deleted && report.state !== 'closed'}>
              <PermissionsCheck>
                <Tooltip
                  tooltip={
                    !canChangeStatus && (
                      <span className="text-color-critical">
                        {formBuilderValidation
                          ? 'Form is missing required fields'
                          : 'Save form changes before changing status'}
                      </span>
                    )
                  }
                >
                  <Button
                    disabled={!canChangeStatus}
                    onClick={toggleShowChangeStatusModal}
                    subtle
                    variant="main"
                  >
                    Change Status
                  </Button>
                </Tooltip>
              </PermissionsCheck>
            </If>

            <ContextMenu dropdownPlace="bottom-right">
              {canDownloadAndExportData && (
                <ContextMenu.Option onClick={toggleShowDownloadModal}>
                  Download PDF
                </ContextMenu.Option>
              )}

              <ContextMenu.Option onClick={toggleShowStatusHistoryModal}>
                Status History
              </ContextMenu.Option>

              {!report.deleted && (
                <>
                  <PermissionsCheck>
                    <ContextMenu.Divider />

                    {report.state !== 'closed' &&
                      !!reportType.dateConfig.allowEditableDate && (
                        <ContextMenu.Option onClick={toggleShowEditDateModal}>
                          Edit Date
                        </ContextMenu.Option>
                      )}

                    <ContextMenu.Option
                      disabled={!canChangeStatus}
                      onClick={() => {
                        if (!canChangeStatus) return
                        toggleShowChangeStatusModal()
                      }}
                    >
                      Change Status
                    </ContextMenu.Option>
                  </PermissionsCheck>

                  <PermissionsCheck>
                    <ContextMenu.Divider />

                    <ContextMenu.Option onClick={toggleShowDeleteModal}>
                      <span className="text-color-critical">Delete Report</span>
                    </ContextMenu.Option>
                  </PermissionsCheck>
                </>
              )}
            </ContextMenu>
          </div>
        </div>
      </div>

      {showStatusHistoryModal && (
        <ShowStatusHistoryModal
          closeHandler={toggleShowStatusHistoryModal}
          report={report}
        />
      )}

      {showChangeStatusModal && canChangeStatus && (
        <ChangeReportStatusModal
          closeHandler={toggleShowChangeStatusModal}
          onComplete={() => {
            toggleShowChangeStatusModal()
            refreshReport()
          }}
          report={report}
        />
      )}

      {showDeleteModal && (
        <ActionModal
          actionText="Delete Report"
          body="Are you sure you want to delete this Report?"
          onCancel={toggleShowDeleteModal}
          onAction={deleteReport}
          title="Delete Report"
          variant="critical"
        />
      )}

      {showDownloadModal && (
        <PickReportRefsModal
          onDownload={handleDownloadReportPDF}
          closeHandler={toggleShowDownloadModal}
        />
      )}

      {showEditDateModal && (
        <Modal
          closeHandler={toggleShowEditDateModal}
          escPressClose
          header="Edit Date"
        >
          <PickReportDate
            actionLabel="Save"
            onChangeValue={setDate}
            onComplete={() => {
              toggleShowEditDateModal()
              onSave?.({date})
            }}
            value={date}
          />
        </Modal>
      )}
    </>
  )
}

export {ReportHeaderSection}
